import { useEffect, useState, useCallback } from 'react';

export const usePaddle = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Paddle
  const initializePaddle = useCallback(async () => {
    // Already fully initialized — nothing to do
    if (window.Paddle && window.Paddle.Initialized) {
      setIsLoaded(true);
      setIsInitialized(true);
      return window.Paddle;
    }

    const doInitialize = () => {
      // Read at call-time so Jest tests can set process.env per-test
      const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
      // Fail-fast: misconfigured build лучше ловить явно, чем получать тихий runtime-fail
      if (!token) {
        throw new Error('[Paddle] REACT_APP_PADDLE_CLIENT_TOKEN is not set. Check your .env file.');
      }
      // Auto-detect sandbox by token prefix
      if (token.startsWith('test_')) {
        window.Paddle.Environment.set('sandbox');
      }
      window.Paddle.Initialize({
        token,
        eventCallback: function(data) {
          // Bridge Paddle v2 eventCallback → window CustomEvents
          // Это позволяет window.addEventListener('paddle:checkout:completed', ...) работать
          if (data && data.name) {
            const eventName = 'paddle:' + data.name.replace(/\./g, ':');
            window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
          }
        }
      });
      // Small delay to ensure Paddle is fully ready after Initialize
      setTimeout(() => {
        setIsLoaded(true);
        setIsInitialized(true);
      }, 1000);
    };

    // Script already in window (loaded by index.html) but not initialized yet
    if (window.Paddle) {
      try {
        doInitialize();
      } catch (error) {
        console.error('Failed to initialize Paddle:', error);
        throw error;
      }
      return window.Paddle;
    }

    // Script not loaded at all — load it dynamically
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.onload = () => {
        try {
          doInitialize();
          resolve(window.Paddle);
        } catch (error) {
          console.error('Failed to initialize Paddle:', error);
          reject(error);
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load Paddle.js'));
      };
      document.head.appendChild(script);
    });
  }, []);

  // Load Paddle on mount
  useEffect(() => {
    initializePaddle().catch(console.error);
  }, [initializePaddle]);

  // Create checkout function
  const createCheckout = useCallback(async (options) => {
    console.log('createCheckout called with options:', options);
    
    if (!window.Paddle) {
      console.error('Paddle not available in window object');
      throw new Error('Paddle not loaded');
    }

    console.log('Paddle object available:', window.Paddle);
    console.log('Paddle.Checkout available:', window.Paddle.Checkout);

    return new Promise(async (resolve, reject) => {
      let cleanupListeners = () => {};
      try {
        // Prepare checkout options according to Paddle's inline checkout API
        let checkoutOptions;

        // If transactionId is provided, use it to open an existing transaction
        if (options.transactionId) {
                  checkoutOptions = {
          transactionId: options.transactionId,
          settings: {
            displayMode: 'inline',
            locale: 'en',
            showAddDiscounts: true,
            allowedPaymentMethods: ['card', 'paypal', 'apple_pay', 'google_pay'],
          },
          frameTarget: 'paddle-checkout-container',
          frameInitialHeight: 700,
          frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
        };
      } else {
        // Fallback to creating transaction with items (legacy approach)
          checkoutOptions = {
            items: options.items || [],
            customerId: options.customerId,
            settings: {
              displayMode: 'inline',
              locale: 'en',
              showAddDiscounts: true,
              allowedPaymentMethods: ['card', 'paypal', 'apple_pay', 'google_pay'],
              successUrl: options.successUrl || 'https://grids.matchmovemachine.com/profile'
            },
            frameTarget: 'paddle-checkout-container',
            frameInitialHeight: 700,
            frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
          };

                  // Add custom data if provided (this is crucial for webhook matching)
        if (options.customData) {
          checkoutOptions.customData = options.customData;
        }

          // Add optional parameters if provided
          if (options.addressId) {
            checkoutOptions.addressId = options.addressId;
          }

          // Add customer authentication token if provided
          if (options.customerAuthToken) {
            checkoutOptions.customerAuthToken = options.customerAuthToken;
          }
        }

        // For inline checkout, we need to specify the container
        if (options.mode === 'inline' && options.container) {
          checkoutOptions.container = options.container;
          console.log('Setting inline checkout container:', options.container);
        }

        // Set up event listeners for the checkout
        const successHandler = (event) => {
          cleanupListeners();
          if (options.successCallback) {
            options.successCallback(event);
          }
          resolve({ success: true, data: event });
        };

        const closeHandler = (event) => {
          cleanupListeners();
          if (options.closeCallback) {
            options.closeCallback();
          }
          resolve({ success: false, closed: true });
        };

        const errorHandler = (event) => {
          cleanupListeners();
          if (options.errorCallback) {
            options.errorCallback(event);
          }
          reject(event);
        };

        // Add event listeners
        window.addEventListener('paddle:checkout:completed', successHandler);
        window.addEventListener('paddle:checkout:closed', closeHandler);
        window.addEventListener('paddle:checkout:error', errorHandler);
        cleanupListeners = () => {
          window.removeEventListener('paddle:checkout:completed', successHandler);
          window.removeEventListener('paddle:checkout:closed', closeHandler);
          window.removeEventListener('paddle:checkout:error', errorHandler);
        };

        // Set customer authentication token if provided
        if (checkoutOptions.customerAuthToken) {
          // Try different methods to set the auth token
          if (window.Paddle.Checkout.setCustomerAuthToken) {
            window.Paddle.Checkout.setCustomerAuthToken(checkoutOptions.customerAuthToken);
          } else if (window.Paddle.setCustomerAuthToken) {
            window.Paddle.setCustomerAuthToken(checkoutOptions.customerAuthToken);
          } else {
            // Try setting it as a global property
            window.Paddle.customerAuthToken = checkoutOptions.customerAuthToken;
            // Also try setting it on the Checkout object
            if (window.Paddle.Checkout) {
              window.Paddle.Checkout.customerAuthToken = checkoutOptions.customerAuthToken;
            }
          }
        }
        
        // Ensure the container element exists before opening checkout
        if (checkoutOptions.container) {
          const containerElement = document.querySelector(checkoutOptions.container);
          if (!containerElement) {
            throw new Error(`Container element ${checkoutOptions.container} not found`);
          }
          
          // Ensure the container is empty and ready for Paddle
          containerElement.innerHTML = '';
          
          // Add a small delay to ensure the container is ready
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Open the checkout with the configured options
        console.log('Final checkout parameters:', checkoutOptions);
        
        // Call Paddle.Checkout.open with proper error handling
        try {
          // Ensure the container is ready before opening checkout
          if (checkoutOptions.container) {
            const containerElement = document.querySelector(checkoutOptions.container);
            if (containerElement) {
              // Clear any existing content
              containerElement.innerHTML = '';
              
              // Ensure the container is properly positioned
              containerElement.style.position = 'relative';
              containerElement.style.width = '100%';
              containerElement.style.minHeight = '450px';
              containerElement.style.overflow = 'hidden';
              containerElement.style.borderRadius = '8px';
              containerElement.style.zIndex = '1';
              
              // Add a small delay to ensure DOM is ready
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          
          window.Paddle.Checkout.open(checkoutOptions);
          if (options.openCallback) {
            options.openCallback();
          }
          
          // Start monitoring for iframe positioning immediately after checkout opens
          if (options.mode === 'inline') {
            // Apply positioning fix immediately and then continue monitoring
            fixIframePositioning(options.container);
          }
        } catch (checkoutError) {
          console.error('Error calling Paddle.Checkout.open:', checkoutError);
          
          // If it's an appendChild error, try to fix the container and retry
          if (checkoutError.message && checkoutError.message.includes('appendChild')) {
            console.log('Detected appendChild error, attempting to fix container...');
            
            if (checkoutOptions.container) {
              const containerElement = document.querySelector(checkoutOptions.container);
              if (containerElement) {
                // Clear and reset the container
                containerElement.innerHTML = '';
                containerElement.style.position = 'relative';
                containerElement.style.width = '100%';
                containerElement.style.minHeight = '450px';
                containerElement.style.overflow = 'hidden';
                containerElement.style.borderRadius = '8px';
                containerElement.style.zIndex = '1';
                
                // Wait a moment and try again
                await new Promise(resolve => setTimeout(resolve, 200));
                
                try {
                  window.Paddle.Checkout.open(checkoutOptions);
                  if (options.openCallback) {
                    options.openCallback();
                  }
                  console.log('Paddle.Checkout.open retry successful');
                  
                  if (options.mode === 'inline') {
                    fixIframePositioning(options.container);
                  }
                } catch (retryError) {
                  console.error('Retry failed:', retryError);
                  throw retryError;
                }
              } else {
                throw checkoutError;
              }
            } else {
              throw checkoutError;
            }
          } else {
            throw checkoutError;
          }
        }

        // Clean up event listeners after a timeout
        setTimeout(() => {
          cleanupListeners();
        }, 300000); // 5 minutes timeout

      } catch (error) {
        console.error('Error in createCheckout:', error);
        if (typeof cleanupListeners === 'function') {
          cleanupListeners();
        }
        reject(error);
      }
    });
  }, []);

  // Function to fix iframe positioning
  const fixIframePositioning = useCallback((containerSelector) => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    // Inject a style tag with the highest specificity to override Paddle's styles
    const styleId = 'paddle-iframe-override';
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.textContent = `
        #paddle-checkout-container iframe.paddle-frame,
        #paddle-checkout-container iframe.paddle-frame-inline,
        #paddle-checkout-container iframe[src*="paddle.com"],
        #paddle-checkout-container iframe[src*="buy.paddle.com"],
        .paddle-checkout-container iframe.paddle-frame,
        .paddle-checkout-container iframe.paddle-frame-inline,
        .paddle-checkout-container iframe[src*="paddle.com"],
        .paddle-checkout-container iframe[src*="buy.paddle.com"] {
          position: relative !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          min-height: 800px !important;
          border: none !important;
          border-radius: 8px !important;
          z-index: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
          display: block !important;
          transform: none !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
          scrollbar-width: none !important;
        }
      `;
      document.head.appendChild(styleTag);
    }

    const fixIframe = () => {
      // Look for any iframe within the container
      let paddleIframe = container.querySelector('iframe');
      
      // If not found in container, look for any Paddle iframe in the document
      if (!paddleIframe) {
        paddleIframe = document.querySelector('iframe.paddle-frame, iframe.paddle-frame-inline, iframe[src*="paddle.com"], iframe[src*="buy.paddle.com"]');
        if (paddleIframe && paddleIframe.parentElement !== container) {
          container.appendChild(paddleIframe);
        }
      }
      
      if (paddleIframe) {
        paddleIframe.style.setProperty('position', 'relative', 'important');
        paddleIframe.style.setProperty('left', '0', 'important');
        paddleIframe.style.setProperty('top', '0', 'important');
        paddleIframe.style.setProperty('width', '100%', 'important');
        paddleIframe.style.setProperty('min-height', '800px', 'important');
        paddleIframe.style.setProperty('overflow', 'hidden', 'important');
        paddleIframe.style.setProperty('border', 'none', 'important');
        paddleIframe.style.setProperty('border-radius', '8px', 'important');
        paddleIframe.style.setProperty('z-index', '1', 'important');
        paddleIframe.style.setProperty('margin', '0', 'important');
        paddleIframe.style.setProperty('padding', '0', 'important');
        paddleIframe.style.setProperty('display', 'block', 'important');
        paddleIframe.style.setProperty('visibility', 'visible', 'important');
        paddleIframe.style.setProperty('opacity', '1', 'important');
        
        // Ensure the iframe is actually in the DOM and visible
        if (paddleIframe.offsetParent === null) {
          container.appendChild(paddleIframe);
        }
      }
    };

    // Initial fix
    fixIframe();

    // Set up a mutation observer to watch for iframe changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'IFRAME' || node.querySelector('iframe')) {
                // Apply fix immediately when iframe is added
                fixIframe();
              }
            }
          });
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // Also watch for style changes on iframes
          if (mutation.target.tagName === 'IFRAME') {
            // Apply fix immediately when style changes
            fixIframe();
          }
        }
      });
    });

    // Observe both the container and the document body for iframe creation
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });
    
    // Also observe the document body for iframes that might be created there
    observer.observe(document.body, {
      childList: true,
      subtree: false
    });

    // Check periodically for the first few seconds
    const interval = setInterval(fixIframe, 1000);
    
    // Create a more frequent interval for the first few seconds
    const frequentInterval = setInterval(fixIframe, 200);
    
    // Clean up after 5 seconds
    setTimeout(() => {
      clearInterval(interval);
      observer.disconnect();
    }, 5000);
    
    // Also clean up frequent interval after 5 seconds
    setTimeout(() => {
      clearInterval(frequentInterval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearInterval(frequentInterval);
      observer.disconnect();
    };
  }, []);

  // Listen for Paddle events
  const addPaddleEventListener = useCallback((eventName, handler) => {
    if (!window.Paddle) return;

    const eventHandler = (event) => {
      console.log(`Paddle event ${eventName}:`, event);
      handler(event);
    };

    window.addEventListener(`paddle:${eventName}`, eventHandler);
    
    return () => {
      window.removeEventListener(`paddle:${eventName}`, eventHandler);
    };
  }, []);

  return {
    isLoaded,
    isInitialized,
    createCheckout,
    fixIframePositioning
  };
};
